import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import uniq from 'lodash/uniq';

import { BasketService } from '../services';
import { useTinyBaskets } from './use-tiny-baskets';
import { Declaration } from '@modules/declarations/types/declaration';
import { DeclarationsService } from '@modules/declarations/services';
import { ErrorAudioUrls } from '@modules/customs/constants/custom_error_audio';
import { playSound } from '@modules/customs/utils/play-sound';

export const useBasketTransfer = () => {
  const baskets = useTinyBaskets();
  const inputRef = useRef<any>(null);
  const [currentBasket, setCurrentBasket] = useState<string | undefined>();
  const [trackCodes, setTrackCodes] = useState<string[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [remoteData, setRemoteData] = useState<Declaration[]>([]);

  const tableData = useMemo(
    () =>
      trackCodes.map((trackCode, index) => {
        const declaration = remoteData.find((declaration) => declaration.trackCode.toString() === trackCode);

        if (declaration) {
          return {
            order: index + 1,
            trackCode,
            isInDns: declaration.isInDns,
            declared: declaration.declared,
            basket: declaration.basket,
            flightName: declaration.flightName,
            parcel: declaration.parcel,
            branch: declaration.branch,
          };
        }

        return {
          order: index + 1,
          trackCode,
          flightName: '',
          declared: null,
          basket: null,
          parcel: null,
        };
      }),
    [remoteData, trackCodes],
  );

  const onSelectBasket = useCallback((value: string) => {
    setCurrentBasket(value);
  }, []);

  const onSearch = useCallback(async (value: string) => {
    inputRef.current?.setValue('');
    const result = await DeclarationsService.getAll({ track_code: value });

    if (result.status === 200 && !!result.data.length) {
      setTrackCodes((prevState) => uniq([value, ...prevState]));
      setRemoteData((prevData) => [...result.data, ...prevData]);

      const declaration = result.data[0];
      if (declaration.declared) {
        playSound(ErrorAudioUrls.declared);
      } else {
        playSound(ErrorAudioUrls.undeclared);
      }
    }
  }, []);

  const onRemove = useCallback((trackCode: string) => {
    setTrackCodes((prevState) => prevState.filter((_trackCode) => _trackCode !== trackCode));
  }, []);

  useEffect(() => {
    const trackCodes = localStorage.getItem('basket.trackCodes');
    setTrackCodes(JSON.parse(trackCodes || '[]'));
  }, []);

  useEffect(() => {
    localStorage.setItem('basket.trackCodes', JSON.stringify(trackCodes));
  }, [trackCodes]);

  const onSubmit = useCallback(async () => {
    setSubmitting(true);
    const result = await BasketService.transfer(trackCodes);

    if (result.status === 200) {
      setTrackCodes([]);
      setCurrentBasket(undefined);
      message.success('Əməliyyat müvəffəqiyyətlə başa çatdı');
    } else {
      setError(new Error(result.data));
    }
    setSubmitting(false);
  }, [trackCodes]);

  const onClear = useCallback(() => {
    setTrackCodes([]);
  }, []);

  return { currentBasket, onSelectBasket, baskets, tableData, error, submitting, onSearch, onSubmit, onClear, onRemove, trackCodes, inputRef };
};
